import React, { useState } from 'react';
import './Contact.css';
import { sendContactForm } from '../api/contact/contact-api';

const IconMapPin = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const IconPhone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const IconClock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormFields {
  name: string;
  company: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
}

const EMPTY: FormFields = { name: '', company: '', email: '', phone: '', subject: '', message: '' };

function validate(fields: FormFields): FormErrors {
  const errors: FormErrors = {};
  if (!fields.name.trim()) errors.name = 'El nombre es obligatorio.';
  if (!fields.email.trim()) {
    errors.email = 'El correo electrónico es obligatorio.';
  } else if (!EMAIL_RE.test(fields.email.trim())) {
    errors.email = 'Ingrese un correo electrónico válido.';
  }
  if (!fields.subject.trim()) errors.subject = 'El asunto es obligatorio.';
  return errors;
}

const Contact: React.FC = () => {
  const [fields, setFields] = useState<FormFields>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    await sendContactForm(
      {
        name: fields.name.trim(),
        companyName: fields.company.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim() || null,
        subject: fields.subject.trim(),
        message: fields.message.trim(),
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          setFields(EMPTY);
        },
        onBadRequest: msg => setServerError(msg || 'Solicitud inválida. Revise los datos e intente de nuevo.'),
        onError: msg => setServerError(msg || 'Ocurrió un error al enviar el mensaje. Intente más tarde.'),
      }
    );
    setSubmitting(false);
  }

  return (
    <div className="contact">

      {/* ── Hero ── */}
      <section className="contact-hero">
        <div className="contact-hero-pattern" aria-hidden="true" />
        <div className="container">
          <div className="contact-hero-eyebrow">Estamos para servirle</div>
          <h1>Contacto</h1>
          <p>Cuéntenos sobre su proceso y nuestros especialistas le brindarán la solución de pesaje más adecuada.</p>
        </div>
      </section>

      {/* ── Split: Info panel + Form ── */}
      <section className="contact-split-section">
        <div className="container">
          <div className="contact-split-wrap">

            {/* Left: dark info panel */}
            <div className="contact-split-info">
              <div className="contact-split-info-top">
                <span className="contact-split-overline">Información de contacto</span>
                <h2 className="contact-split-heading">Hablemos sobre su proyecto</h2>
                <p className="contact-split-sub">
                  Nuestros especialistas le asesorarán en la solución de pesaje más adecuada para su industria.
                </p>
              </div>

              <ul className="contact-split-items" aria-label="Datos de contacto">
                <li className="contact-split-item">
                  <span className="contact-split-item-icon"><IconMapPin /></span>
                  <div className="contact-split-item-body">
                    <span className="contact-split-item-label">Oficina Principal</span>
                    <span className="contact-split-item-value">
                      Av. Tabachines 3750, Col. Loma Bonita Ejidal<br />
                      Zapopan, Jalisco, C.P. 45085
                    </span>
                  </div>
                </li>

                <li className="contact-split-item">
                  <span className="contact-split-item-icon"><IconPhone /></span>
                  <div className="contact-split-item-body">
                    <span className="contact-split-item-label">Teléfonos</span>
                    <span className="contact-split-item-value">
                      <a href="tel:+523336349050">(33) 3634 9050</a><br />
                      <a href="tel:+523336326306">(33) 3632 6306</a><br />
                      <a href="tel:+523336349322">(33) 3634 9322</a>
                    </span>
                  </div>
                </li>

                <li className="contact-split-item">
                  <span className="contact-split-item-icon"><IconMail /></span>
                  <div className="contact-split-item-body">
                    <span className="contact-split-item-label">Correo electrónico</span>
                    <a href="mailto:abc@abcscale.mx" className="contact-split-item-value contact-split-item-link">
                      abc@abcscale.mx
                    </a>
                  </div>
                </li>

                <li className="contact-split-item">
                  <span className="contact-split-item-icon"><IconClock /></span>
                  <div className="contact-split-item-body">
                    <span className="contact-split-item-label">Horario de atención</span>
                    <span className="contact-split-item-value">
                      Lunes a Viernes<br />9:00 — 18:00 hrs
                    </span>
                  </div>
                </li>
              </ul>

              <div className="contact-split-deco-circle contact-split-deco-circle--lg" aria-hidden="true" />
              <div className="contact-split-deco-circle contact-split-deco-circle--sm" aria-hidden="true" />
            </div>

            {/* Right: form panel */}
            <div className="contact-split-form-panel">
              <div className="contact-split-form-header">
                <h2 className="contact-split-form-title">Envíenos un mensaje</h2>
                <p className="contact-split-form-sub">
                  Nuestro equipo técnico le responderá en menos de 24 horas hábiles.
                </p>
              </div>

              {submitted ? (
                <div className="contact-success" role="alert">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <div>
                    <strong>Mensaje enviado</strong>
                    <p>Gracias por contactarnos. Le responderemos en menos de 24 horas hábiles.</p>
                  </div>
                </div>
              ) : (
                <form className="contact-form" noValidate onSubmit={handleSubmit}>

                  <div className="form-row">
                    <div className={`form-group${errors.name ? ' form-group--error' : ''}`}>
                      <label htmlFor="name">Nombre completo <span className="form-required">*</span></label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Ej. Juan Martínez"
                        autoComplete="name"
                        value={fields.name}
                        onChange={handleChange}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                      />
                      {errors.name && <span className="form-error-msg" id="name-error" role="alert">{errors.name}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Empresa</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Nombre de su empresa"
                        autoComplete="organization"
                        value={fields.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className={`form-group${errors.email ? ' form-group--error' : ''}`}>
                      <label htmlFor="email">Correo electrónico <span className="form-required">*</span></label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="correo@empresa.com"
                        autoComplete="email"
                        value={fields.email}
                        onChange={handleChange}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      {errors.email && <span className="form-error-msg" id="email-error" role="alert">{errors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Teléfono</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="(33) 0000 0000"
                        autoComplete="tel"
                        value={fields.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className={`form-group${errors.subject ? ' form-group--error' : ''}`}>
                    <label htmlFor="subject">Asunto <span className="form-required">*</span></label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="Ej. Cotización de báscula camionera"
                      value={fields.subject}
                      onChange={handleChange}
                      aria-describedby={errors.subject ? 'subject-error' : undefined}
                    />
                    {errors.subject && <span className="form-error-msg" id="subject-error" role="alert">{errors.subject}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Mensaje <span className="form-required">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      placeholder="Describa brevemente sus necesidades de pesaje: tipo de equipo, capacidad requerida, aplicación, etc."
                      value={fields.message}
                      onChange={handleChange}
                    />
                  </div>

                  {serverError && (
                    <div className="contact-server-error" role="alert">{serverError}</div>
                  )}

                  <div className="form-footer">
                    <p className="form-note">
                      Los campos marcados con <span className="form-required">*</span> son obligatorios.
                    </p>
                    <button type="submit" className="btn-submit" disabled={submitting}>
                      {submitting ? 'Enviando…' : 'Enviar mensaje'}
                      {!submitting && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                      )}
                    </button>
                  </div>

                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Branches strip ── */}
      <section className="contact-branches">
        <div className="container">
          <span className="contact-branches-label">Sucursales</span>
          <div className="contact-branches-grid">

            <div className="contact-branch contact-branch--main">
              <span className="contact-branch-icon"><IconMapPin /></span>
              <div>
                <span className="contact-branch-name">Guadalajara</span>
                <span className="contact-branch-note">Oficina principal</span>
              </div>
            </div>

            <div className="contact-branch">
              <span className="contact-branch-icon"><IconMapPin /></span>
              <span className="contact-branch-name">Ciudad de México</span>
            </div>

            <div className="contact-branch">
              <span className="contact-branch-icon"><IconMapPin /></span>
              <span className="contact-branch-name">Monterrey</span>
            </div>

            <div className="contact-branch">
              <span className="contact-branch-icon"><IconMapPin /></span>
              <span className="contact-branch-name">San Luis Potosí</span>
            </div>

          </div>
        </div>
      </section>

      {/* ── Privacy notice ── */}
      <section className="privacy-notice">
        <div className="container">
          <h3>Aviso de Privacidad</h3>
          <p>
            Sus datos personales son tratados de forma confidencial y utilizados exclusivamente
            para atender su solicitud y brindarle nuestros servicios, de conformidad con la
            Ley Federal de Protección de Datos Personales en Posesión de los Particulares.
          </p>
        </div>
      </section>

    </div>
  );
};

export default Contact;
